import { doc, getDoc, setDoc, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase/config';

// Formula configuration for calculated inventory fields
export interface FormulaConfig {
  id: string;
  name: string;
  description: string;
  formula: string;
  variables: string[];
  enabled: boolean;
}

export interface InventoryFormulas {
  barcode: FormulaConfig;
  age: FormulaConfig;
  newMelt: FormulaConfig;
  dpremWws: FormulaConfig;
  wholesale: FormulaConfig;
  margin: FormulaConfig;
  day0: FormulaConfig;
  ebay0: FormulaConfig;
  deny: FormulaConfig;
  updatedAt?: Date;
  updatedBy?: string;
}

// Default formulas that match the current hardcoded logic
export const DEFAULT_FORMULAS: InventoryFormulas = {
  barcode: {
    id: 'barcode',
    name: 'Barcode',
    description: 'Generate barcode from SKU (removes PM prefix and non-digits)',
    formula: 'sku.replace(/^PM/i, "").replace(/\\D/g, "")',
    variables: ['sku'],
    enabled: true
  },
  age: {
    id: 'age',
    name: 'Age (Days)',
    description: 'Days since the item was added',
    formula: 'Math.floor((today - date) / (1000 * 60 * 60 * 24))',
    variables: ['date', 'today'],
    enabled: true
  },
  newMelt: {
    id: 'newMelt',
    name: 'New Melt Value',
    description: 'Melt value based on metal type, weight, and spot prices (tiered for silver/sterling)',
    formula: `
      (function() {
        if (!metal || !weightGrams) return 0;

        const grams = Number(weightGrams) || 0;
        const m = String(metal).trim().toLowerCase();

        const OZ_TO_G = 31.1035;

        // metalPrices are per troy ounce, convert to per gram
        const silverPerGram = (metalPrices.silver ?? metalPrices.silver999 ?? metalPrices.ag ?? 0) / OZ_TO_G;
        const goldPerGram = (metalPrices.gold ?? metalPrices.au ?? 0) / OZ_TO_G;
        const platinumPerGram = (metalPrices.platinum ?? metalPrices.pt ?? 0) / OZ_TO_G;
        const palladiumPerGram = (metalPrices.palladium ?? metalPrices.pd ?? 0) / OZ_TO_G;

        // Tier multiplier by weight for silver/sterling
        const tierMult = (g) => {
          if (g < 26) return 4;
          if (g < 51) return 3;
          if (g < 101) return 2.5;
          if (g < 201) return 1.75;
          if (g < 501) return 1.25;
          return 1;
        };

        if (m === "silver 999") return grams * tierMult(grams) * silverPerGram;
        if (m === "sterling") return grams * 0.925 * tierMult(grams) * silverPerGram;
        if (m === "gold filled") return grams * 3;
        if (m === "gold 10k") return grams * (10 / 24) * goldPerGram;
        if (m === "gold 14k") return grams * (14 / 24) * goldPerGram;
        if (m === "gold 18k") return grams * (18 / 24) * goldPerGram;
        if (m === "gold 20k") return grams * (20 / 24) * goldPerGram;
        if (m === "gold 22k") return grams * (22 / 24) * goldPerGram;
        if (m === "gold 24k") return grams * goldPerGram;
        if (m === "platinum") return grams * platinumPerGram;
        if (m === "palladium") return grams * palladiumPerGram;
        if (m === "coin") return grams * silverPerGram + 25;
        if (m === "other" || m === "watch" || m === "") return 0;

        return 0;
      })()
    `,
    variables: ['metal', 'weightGrams', 'metalPrices'],
    enabled: true
  },
  dpremWws: {
    id: 'dpremWws',
    name: 'DPrem/WWS',
    description: 'Designer Premium - can be manual entry or calculated from retail and melt',
    formula: '0',  // Default: no automatic calculation (manual entry)
    variables: ['retail', 'newMelt'],
    enabled: false  // Disabled by default - use manual entry
  },
  wholesale: {
    id: 'wholesale',
    name: 'Wholesale (WS)',
    description: 'Wholesale price calculation',
    formula: 'Math.max(cost, newMelt) + stones + dpremWws',
    variables: ['cost', 'newMelt', 'stones', 'dpremWws'],
    enabled: true
  },
  margin: {
    id: 'margin',
    name: 'Margin %',
    description: 'Profit margin percentage',
    formula: 'price > 0 ? ((price - wholesale) / price) * 100 : 0',
    variables: ['price', 'wholesale'],
    enabled: true
  },
  day0: {
    id: 'day0',
    name: 'Day 0 Price',
    description: 'Starting price based on wholesale and item characteristics',
    formula: `
      (function() {
        if (!wholesale || wholesale <= 0) return 0;
        const ws = wholesale;
        const g = weightGrams;
        
        // PANDORA items
        if (type === 'PANDORA') {
          return Math.ceil(ws / 10) * 10;
        }
        
        // Gold items - weight-based multipliers
        if (metal && metal.startsWith('Gold')) {
          let mult = g < 5 ? 1.6 : g < 11 ? 1.5 : g < 31 ? 1.4 : 1.35;
          return Math.ceil((ws * mult) / 10) * 10;
        }
        
        // High value items ($5000+)
        if (ws >= 5000) {
          let mult = ws < 20000 ? 1.377 : ws < 50000 ? 1.357 : 1.327;
          return Math.ceil((ws * mult) / 10) * 10;
        }
        
        // Medium value ($1000-$5000)
        if (ws >= 1000) {
          return Math.ceil((ws * 1.527) / 10) * 10;
        }
        
        // Low value (<$1000)
        let val = ws <= 50 ? ws * 1.627 + 25 : ws < 100 ? ws * 1.627 + 20 : ws * 1.627;
        return Math.ceil(val / 10) * 10;
      })()
    `,
    variables: ['wholesale', 'weightGrams', 'metal', 'type'],
    enabled: true
  },
  ebay0: {
    id: 'ebay0',
    name: 'eBay Day 0',
    description: 'eBay starting price (includes platform fees)',
    formula: `
      (function() {
        if (!wholesale || wholesale <= 0) return 0;
        const ws = wholesale;
        
        // Watch-specific tiered calculation
        if (metal === "Watch") {
          let value;
          if (ws < 1000) value = ws * 1.15;
          else if (ws < 2500) value = ws * 1.12;
          else if (ws < 5000) value = ws * 1.11;
          else if (ws < 7500) value = ws * 1.09;
          else if (ws < 10000) value = ws * 1.085;
          else if (ws < 15000) value = ws * 1.07;
          else value = ws * 1.06;
          return Math.ceil(value / 10) * 10;
        }
        
        // Non-watch items: tiered calculation based on day0
        let value;
        if (ws >= 7500) {
          value = day0 + ((day0 - 7500) * 0.0235) + 546;
        } else if (ws >= 1000) {
          value = day0 + ((day0 - 1000) * 0.065) + 123.5;
        } else {
          value = day0 * 1.1235;
        }
        return Math.ceil(value / 10) * 10;
      })()
    `,
    variables: ['wholesale', 'day0', 'metal'],
    enabled: true
  },
  deny: {
    id: 'deny',
    name: 'Deny Price',
    description: 'Minimum acceptable price (deny below this)',
    formula: `
      (function() {
        if (!wholesale || wholesale <= 0) return 0;
        const ws = wholesale;
        if (ws < 100) {
          return (ws * 1.227) + 7;
        }
        return ws * 1.227;
      })()
    `,
    variables: ['wholesale'],
    enabled: true
  }
};

const COLLECTION = 'settings';
const DOC_ID = 'inventory-formulas';

// Get formulas from Firestore (or defaults if not set)
export const getInventoryFormulas = async (): Promise<InventoryFormulas> => {
  try {
    const docRef = doc(db, COLLECTION, DOC_ID);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        ...DEFAULT_FORMULAS,
        ...data,
        updatedAt: data.updatedAt?.toDate?.() || new Date()
      };
    }
    
    return DEFAULT_FORMULAS;
  } catch (error) {
    console.error('Error loading formulas:', error);
    return DEFAULT_FORMULAS;
  }
};

// Save formulas to Firestore
export const saveInventoryFormulas = async (
  formulas: Partial<InventoryFormulas>,
  updatedBy: string
): Promise<void> => {
  try {
    const docRef = doc(db, COLLECTION, DOC_ID);
    await setDoc(docRef, {
      ...formulas,
      updatedAt: Timestamp.fromDate(new Date()),
      updatedBy
    }, { merge: true });
  } catch (error) {
    console.error('Error saving formulas:', error);
    throw error;
  }
};

// Subscribe to formula changes
export const subscribeInventoryFormulas = (
  callback: (formulas: InventoryFormulas) => void
): (() => void) => {
  const docRef = doc(db, COLLECTION, DOC_ID);
  
  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      const data = docSnap.data();
      callback({
        ...DEFAULT_FORMULAS,
        ...data,
        updatedAt: data.updatedAt?.toDate?.() || new Date()
      });
    } else {
      callback(DEFAULT_FORMULAS);
    }
  }, (error) => {
    console.error('Error subscribing to formulas:', error);
    callback(DEFAULT_FORMULAS);
  });
};

// Reset a single formula to default
export const resetFormulaToDefault = async (
  formulaId: keyof InventoryFormulas,
  updatedBy: string
): Promise<void> => {
  if (formulaId === 'updatedAt' || formulaId === 'updatedBy') return;
  
  const defaultFormula = DEFAULT_FORMULAS[formulaId as keyof typeof DEFAULT_FORMULAS];
  if (!defaultFormula) return;
  
  await saveInventoryFormulas(
    { [formulaId]: defaultFormula },
    updatedBy
  );
};

// Reset all formulas to defaults
export const resetAllFormulasToDefault = async (updatedBy: string): Promise<void> => {
  await saveInventoryFormulas(DEFAULT_FORMULAS, updatedBy);
};

// Safely evaluate a formula with given variables
export const evaluateFormula = (
  formula: string,
  variables: Record<string, any>
): number | string => {
  try {
    // Create a safe function context with the variables
    const varNames = Object.keys(variables);
    const varValues = Object.values(variables);
    
    // Create function that returns the formula result
    const fn = new Function(...varNames, `"use strict"; return (${formula});`);
    const result = fn(...varValues);
    
    // Handle NaN, Infinity, etc.
    if (typeof result === 'number') {
      if (!isFinite(result)) return 0;
      return Math.round(result * 100) / 100;
    }
    
    return result;
  } catch (error) {
    console.error('Formula evaluation error:', error, { formula, variables });
    return 0;
  }
};

// Validate a formula before saving
export const validateFormula = (
  formula: string,
  testVariables: Record<string, any>
): { valid: boolean; error?: string; result?: any } => {
  try {
    const result = evaluateFormula(formula, testVariables);
    return { valid: true, result };
  } catch (error: any) {
    return { valid: false, error: error.message || 'Invalid formula' };
  }
};
